import { endOfDay, format, parse, parseISO } from 'date-fns';

import { CreateServiceAndResourceRequest } from '@classpass/protobuf-definitions/lib/web/grpc/msinventory/msinventory';
import { GetClassPricingRulesResponse } from '@classpass/protobuf-definitions/lib/web/models/partners/class_pricing_rules';
import { CreateFoodAndBeverageItemPricingCaseRequest } from '@classpass/protobuf-definitions/lib/web/models/partners/fb_item_pricing_case';
import _isEqual from 'lodash/isEqual';

import { CLASS_ENTITY_TYPE } from 'constants/logic';
import URLS from 'constants/urls';
import { transformKeysToCamelCase, transformKeysToSnakeCase, transformV2ClientResponse } from 'helpers/core';
import { reportError, reportWarning } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { adjustDateForTimezone, dateObjectToNaiveISOString } from 'helpers/intl-fns';
import segment from 'helpers/segment';

import {
  DEFAULT_AVAILABILITY,
  DEFAULT_DURATION,
  FULL_TO_SHORT_DAY_MAP,
  ISO_STRING_FOR_INDEFINITE_UNAVAILABILITY_END_DATETIME,
  ITEM_AVAILABILITY_STATUS,
  OTHER_CATEGORY_NAME,
  OUT_OF_STOCK_DESCRIPTION,
  SHORT_TO_FULL_DAY_MAP,
} from './Items.constants';

export const ITEM_CATEGORY_UPDATED_EVENT = 'itemCategoryUpdated';
export const itemCategoryUpdatedEventTarget = new EventTarget();

async function fetchResources(venueId) {
  const response = await httpClient.get(URLS.manageServices.resources.list(venueId, { page: 1, pageSize: 1000 }));
  return transformV2ClientResponse(response.data);
}

async function fetchServices(venueId) {
  const response = await httpClient.get(URLS.manageServices.services.list(venueId, { page: 1, pageSize: 1000 }));
  return transformV2ClientResponse(response)?.services;
}

async function fetchClasstivityClasses(venueId) {
  const params = transformKeysToSnakeCase({
    onlyActive: false,
    entityType: CLASS_ENTITY_TYPE.APPOINTMENT,
    includeClassPricing: false,
    includeDisabled: false,
  });

  const response = await httpClient.get(URLS.venue.classes(venueId), { params });
  return transformV2ClientResponse(response.data);
}

async function fetchClassPricing(venueId) {
  const response = await httpClient.get(URLS.venue.classPricing(venueId));
  return transformKeysToCamelCase(GetClassPricingRulesResponse.fromJSON(response.data));
}

async function fetchClassDetails(classId) {
  const response = await httpClient.get(URLS.class.detailsRead(classId));
  return transformV2ClientResponse(response);
}

async function createServiceAndResource(
  { venueId, siteId },
  { itemName, itemDescription, duration },
  itemAvailability
) {
  const url = URLS.items.serviceAndResource(venueId);
  const requestBody = CreateServiceAndResourceRequest.toJSON({
    availability_recurrence: transformKeysToSnakeCase(itemAvailability),
    description: itemDescription,
    duration,
    msi_venue_external_id: siteId,
    name: itemName,
  });

  const response = await httpClient.post(url, requestBody);
  const serviceAndResource = transformV2ClientResponse(response);

  return serviceAndResource;
}

async function updateCategory(classId, newTagIds, oldTagIds) {
  const url = URLS.class.update(classId);
  const response = await httpClient.patch(url, { old_tag_ids: oldTagIds, new_tag_ids: newTagIds });

  itemCategoryUpdatedEventTarget.dispatchEvent(new CustomEvent(ITEM_CATEGORY_UPDATED_EVENT));

  return response;
}

export async function submitMenuPricing({ venueId, currencyCode }, { itemName, menuPrice, linkToMenu }) {
  const url = URLS.items.submitMenuPricing(venueId);
  const requestBody = CreateFoodAndBeverageItemPricingCaseRequest.toJSON({
    item_name: itemName,
    menu_price: { currency_code: currencyCode, formatted_price_in_major_units: menuPrice },
    menu_link: linkToMenu,
  });

  return httpClient.post(url, requestBody, {
    ignoreInternalError: true,
  });
}

function tryToUpdateCategory({ venueId }, classId, itemName, { newTagIds, oldTagIds = [] }, triesCounter = 0) {
  if (classId) {
    updateCategory(classId, newTagIds, oldTagIds);
    reportWarning("Successfully update an Item's category", { itemName, venueId });
  } else {
    reportWarning("Failed to update an Item's category", { itemName, venueId });

    setTimeout(async () => {
      const classes = await fetchClasstivityClasses(venueId);
      const classtivityClass = classes.find(({ className }) => className === itemName);

      tryToUpdateCategory({ venueId }, classtivityClass?.classId, itemName, { newTagIds, oldTagIds }, triesCounter + 1);
    }, 5000 * 2 ** triesCounter);
  }
}

const formatAvailabilityRecurrenceForCreate = availabilityRecurrence => {
  return availabilityRecurrence.reduce((acc, dayAvailability) => {
    const { times, weekday, available } = dayAvailability;

    if (available) {
      times.forEach(({ startTime, endTime }) => {
        acc.push({ endTime, startTime, weekday });
      });
    }
    return acc;
  }, []);
};

const formatAvailabilityRecurrenceForUpdate = resourceAvailabilityRecurrence => {
  const availabilityRecurrence = resourceAvailabilityRecurrence.reduce((acc, dayAvailability) => {
    const { times, weekday, available } = dayAvailability;
    const shortDayName = FULL_TO_SHORT_DAY_MAP[weekday];

    if (shortDayName && available) {
      times.forEach(({ startTime, endTime }) => {
        const formattedStartTime = format(parse(startTime, 'HH:mm', new Date()), 'HH:mm:ss');
        const formattedEndTime = format(parse(endTime, 'HH:mm', new Date()), 'HH:mm:ss');

        acc.push({ end_time: formattedEndTime, start_time: formattedStartTime, weekday: shortDayName });
      });
    }
    return acc;
  }, []);

  return availabilityRecurrence;
};

const formatAvailabilityRecurrenceForItems = resourceAvailabilityRecurrence => {
  const currentAvailabilityMap = resourceAvailabilityRecurrence.reduce((acc, dayAvailability) => {
    const { weekday, startTime, endTime } = dayAvailability;
    const fullDayName = SHORT_TO_FULL_DAY_MAP[weekday];
    if (fullDayName) {
      const formattedStartTime = format(parse(startTime, 'HH:mm:ss', new Date()), 'HH:mm');
      const formattedEndTime = format(parse(endTime, 'HH:mm:ss', new Date()), 'HH:mm');

      if (!acc[fullDayName]) {
        acc[fullDayName] = {
          ...dayAvailability,
          weekday: fullDayName,
          times: [],
        };
      }
      acc[fullDayName].times.push({ startTime: formattedStartTime, endTime: formattedEndTime });
    }
    return acc;
  }, {});

  return DEFAULT_AVAILABILITY.map(dayAvailability => {
    const { weekday } = dayAvailability;
    if (currentAvailabilityMap[weekday]) {
      return {
        ...currentAvailabilityMap[weekday],
        available: true,
      };
    }
    return {
      ...dayAvailability,
      available: false,
    };
  });
};

export async function createItem(venue, item, itemAvailability) {
  const formattedAvailability = formatAvailabilityRecurrenceForCreate(itemAvailability);
  try {
    const { classId } = await createServiceAndResource(venue, item, formattedAvailability);
    const { category, itemName } = item;
    tryToUpdateCategory(venue, classId, itemName, { newTagIds: [category.id] });
  } catch (error) {
    reportError('Failed to create an Item', error);

    return Promise.reject(error);
  }
}

function prepareItemUpdateBody(item) {
  return transformKeysToSnakeCase(
    {
      availabilityOverrides: item.availabilityOverrides,
      availabilityRecurrence: item.availabilityRecurrence,
      name: item.name,
      serviceIds: item.serviceIds,
      timeBlocks: item.timeBlocks,
    },
    ['_delete']
  );
}

function markAllCurrentTimeBlocksToBeDeleted(timeBlocks) {
  return timeBlocks.map(timeBlock => ({ ...timeBlock, _delete: true }));
}

export async function makeItemAvailable(venueId, item) {
  const updatedItem = {
    ...item,
    availabilityRecurrence: formatAvailabilityRecurrenceForUpdate(item.availabilityRecurrence),
    timeBlocks: markAllCurrentTimeBlocksToBeDeleted(item.currentTimeBlocks),
  };

  const url = URLS.manageServices.resources.update(venueId, item.resourceId);
  return httpClient.put(url, prepareItemUpdateBody(updatedItem));
}

const getUnavailableForTodayTimeBlocks = (venueTimezone, currentTimeBlocks) => {
  const currentDate = new Date();
  const currentDateAdjustedForTimezone = adjustDateForTimezone(currentDate, venueTimezone);
  const endOfCurrentDay = endOfDay(currentDateAdjustedForTimezone);

  const newTimeBlock = {
    start_datetime: dateObjectToNaiveISOString(currentDateAdjustedForTimezone),
    end_datetime: dateObjectToNaiveISOString(endOfCurrentDay),
    description: OUT_OF_STOCK_DESCRIPTION,
  };

  return [...markAllCurrentTimeBlocksToBeDeleted(currentTimeBlocks), newTimeBlock];
};

const getIndefinitelyUnavailableTimeBlocks = venueTimezone => {
  const currentDate = new Date();
  const currentDateAdjustedForTimezone = adjustDateForTimezone(currentDate, venueTimezone);
  const endOfTime = ISO_STRING_FOR_INDEFINITE_UNAVAILABILITY_END_DATETIME;

  const newTimeBlock = {
    start_datetime: dateObjectToNaiveISOString(currentDateAdjustedForTimezone),
    end_datetime: endOfTime,
    description: OUT_OF_STOCK_DESCRIPTION,
  };

  return [newTimeBlock];
};

export async function makeItemUnavailableToday(venueId, venueTimezone, item) {
  const timeBlocksForToday = getUnavailableForTodayTimeBlocks(venueTimezone, item.currentTimeBlocks);

  const updatedItem = {
    ...item,
    availabilityRecurrence: formatAvailabilityRecurrenceForUpdate(item.availabilityRecurrence),
    timeBlocks: timeBlocksForToday,
  };

  const url = URLS.manageServices.resources.update(venueId, item.resourceId);
  return httpClient.put(url, prepareItemUpdateBody(updatedItem));
}

export async function makeItemUnavailableIndefinitely(venueId, venueTimezone, item) {
  const timeBlocks = getIndefinitelyUnavailableTimeBlocks(venueTimezone);

  const updatedItem = {
    ...item,
    availabilityRecurrence: formatAvailabilityRecurrenceForUpdate(item.availabilityRecurrence),
    timeBlocks,
  };

  const url = URLS.manageServices.resources.update(venueId, item.resourceId);
  return httpClient.put(url, prepareItemUpdateBody(updatedItem));
}

const groupAndSortItems = items => {
  const groupedItems = items.reduce((itemsMap, item) => {
    const { category } = item;
    const categoryKey = category || OTHER_CATEGORY_NAME;

    if (!itemsMap[categoryKey]) {
      itemsMap[categoryKey] = [];
    }
    itemsMap[categoryKey].push(item);
    return itemsMap;
  }, {});

  const sortedItemsByCategory = Object.entries(groupedItems)
    .map(([category, itemsList]) => ({
      category,
      items: itemsList.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => {
      if (a.category === OTHER_CATEGORY_NAME) return 1;
      if (b.category === OTHER_CATEGORY_NAME) return -1;
      return a.category.localeCompare(b.category);
    });

  return sortedItemsByCategory;
};

const getItemsAvailabilityStatusFromResources = (resources, venueTimezone) => {
  return resources.map(({ id, timeBlocks, availabilityRecurrence }) => {
    const currentDate = new Date();
    const currentDateTime = adjustDateForTimezone(currentDate, venueTimezone);
    const endOfCurrentDay = endOfDay(adjustDateForTimezone(currentDate, venueTimezone));
    const formattedAvailabilityRecurrence = formatAvailabilityRecurrenceForItems(availabilityRecurrence);

    if (timeBlocks.length === 0) {
      return {
        resourceId: id,
        availabilityStatus: ITEM_AVAILABILITY_STATUS.AVAILABLE,
        currentTimeBlocks: [],
        availabilityRecurrence: formattedAvailabilityRecurrence,
      };
    }

    const { maxEndDatetime, currentTimeBlocks } = timeBlocks.reduce(
      (acc, timeBlock) => {
        const { endDatetime, startDatetime } = timeBlock;
        const parsedEndDatetime = parseISO(endDatetime);
        const parsedStartDatetime = parseISO(startDatetime);

        if (currentDateTime > parsedStartDatetime && currentDateTime < parsedEndDatetime) {
          acc.currentTimeBlocks.push(timeBlock);
          acc.maxEndDatetime = parsedEndDatetime > acc.maxEndDatetime ? parsedEndDatetime : acc.maxEndDatetime;
        }
        return acc;
      },
      { maxEndDatetime: null, currentTimeBlocks: [] }
    );

    let availabilityStatus;
    if (maxEndDatetime) {
      if (maxEndDatetime > endOfCurrentDay) {
        availabilityStatus = ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY;
      } else {
        availabilityStatus = ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY;
      }
    } else {
      availabilityStatus = ITEM_AVAILABILITY_STATUS.AVAILABLE;
    }

    return {
      resourceId: id,
      availabilityStatus,
      currentTimeBlocks,
      availabilityRecurrence: formattedAvailabilityRecurrence,
    };
  });
};

const getServicesWithAvailabilityStatus = (services, availabilities) => {
  return services.reduce((acc, service) => {
    const { id, resources } = service;

    if (resources.length === 0) {
      reportWarning(`Item Service ${id} has no linked resources.`);
      return acc;
    }

    if (resources.length > 1) {
      reportWarning(`Item Service ${id} has more than one linked resource.`);
    }

    const [resource] = resources;
    const resourceAvailability = resource
      ? availabilities.find(availability => availability.resourceId === resource.id)
      : { availabilityStatus: ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY };

    acc.push({
      ...service,
      ...resourceAvailability,
    });

    return acc;
  }, []);
};

const getServicesWithClassDetails = async (classes, services) => {
  const servicesWithClassDetails = await Promise.all(
    classes.map(async ({ classId, externalClassId, category }) => {
      const mappedService = services.find(service => service.openApiId === externalClassId);

      if (!mappedService) {
        return null;
      }

      let activeTagIds = [];
      if (!category && classId) {
        try {
          const classDetails = await fetchClassDetails(classId);
          activeTagIds = classDetails?.activeTagIds;
        } catch (error) {
          reportError(`Could not get class details for classId: ${classId}`, error);
        }
      }

      const { id, name, description, openApiId, resources } = mappedService;
      return {
        activeTagIds,
        category,
        classId,
        externalClassId,
        id,
        name,
        description,
        openApiId,
        resources,
      };
    })
  );
  return servicesWithClassDetails.filter(Boolean);
};

const getItemsWithPayoutRate = (servicesWithClassDetails, classPricing) => {
  return servicesWithClassDetails.map(serviceWithClass => {
    const { classId } = serviceWithClass;
    const { pricingRules } = classPricing;
    const pricing = pricingRules.find(({ classId: pricingClassId }) => pricingClassId === classId);

    return {
      ...serviceWithClass,
      payoutRate: pricing ? parseFloat(pricing.classPriceFormatted) : null,
    };
  });
};

export async function getTransformedItems(venueId, venueTimezone) {
  const [resources, services, classes, classPricing] = await Promise.all([
    fetchResources(venueId),
    fetchServices(venueId),
    fetchClasstivityClasses(venueId),
    fetchClassPricing(venueId),
  ]);

  const itemsAvailability = getItemsAvailabilityStatusFromResources(resources, venueTimezone);
  const servicesWithClassDetails = await getServicesWithClassDetails(classes, services);
  const servicesWithAvailability = getServicesWithAvailabilityStatus(servicesWithClassDetails, itemsAvailability);
  const itemsWithPayoutRate = getItemsWithPayoutRate(servicesWithAvailability, classPricing);
  const groupedItems = groupAndSortItems(itemsWithPayoutRate);

  return { flatItems: itemsWithPayoutRate, groupedItems };
}

const updateService = async (venueId, serviceId, updateServicePayload) => {
  const endpoint = URLS.manageServices.services.update(venueId, serviceId);
  return httpClient.put(endpoint, updateServicePayload);
};

const updateResource = async (venueId, resourceId, updateResourcePayload) => {
  const endpoint = URLS.manageServices.resources.update(venueId, resourceId);
  return httpClient.put(endpoint, updateResourcePayload);
};

const updateAllItemsAvailability = (allItems, venueId, availabilityRecurrence) => {
  return allItems.map(item => {
    const { currentTimeBlocks, id: serviceId, name, resourceId } = item;

    const updateResourcePayload = {
      availabilityOverrides: [],
      availabilityRecurrence: formatAvailabilityRecurrenceForUpdate(availabilityRecurrence),
      name,
      serviceIds: [serviceId],
      timeBlocks: currentTimeBlocks,
    };

    return updateResource(venueId, resourceId, transformKeysToSnakeCase(updateResourcePayload, ['_delete']));
  });
};

export const updateItem = async (
  allItems,
  venue,
  foodAndBeverageL1Tag,
  shouldUpdateAllItemAvailabilities,
  currentValues,
  formValues
) => {
  const { venueId, timezone: venueTimezone } = venue;
  const { availabilityRecurrence, availabilityStatus, name, description, category } = formValues;
  const {
    id: serviceId,
    resourceId,
    classId,
    currentTimeBlocks,
    availabilityRecurrence: currentAvailabilityRecurrence,
    availabilityStatus: currentAvailabilityStatus,
    name: currentName,
    description: currentDescription,
    category: currentCategory,
    currentCategoryTag,
  } = currentValues;

  const promises = [];

  if (currentName !== name || currentDescription !== description) {
    const updateServicePayload = {
      name,
      description,
      duration: DEFAULT_DURATION,
      practitionerIds: [],
      resourceIds: [resourceId],
    };

    promises.push(updateService(venueId, serviceId, transformKeysToSnakeCase(updateServicePayload)));
  }

  if (
    currentName !== name ||
    currentAvailabilityStatus !== availabilityStatus ||
    !_isEqual(currentAvailabilityRecurrence, availabilityRecurrence)
  ) {
    let timeblocks = [];
    if (availabilityStatus !== currentAvailabilityStatus) {
      switch (availabilityStatus) {
        case ITEM_AVAILABILITY_STATUS.AVAILABLE:
          timeblocks = markAllCurrentTimeBlocksToBeDeleted(currentTimeBlocks);
          break;
        case ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY:
          timeblocks = getUnavailableForTodayTimeBlocks(venueTimezone, currentTimeBlocks);
          break;
        case ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY:
          timeblocks = getIndefinitelyUnavailableTimeBlocks(venueTimezone);
          break;
        default:
          break;
      }
    }

    const updateResourcePayload = {
      availabilityOverrides: [],
      availabilityRecurrence: formatAvailabilityRecurrenceForUpdate(availabilityRecurrence),
      name,
      serviceIds: [serviceId],
      timeBlocks: timeblocks.length > 0 ? timeblocks : currentTimeBlocks,
    };

    promises.push(updateResource(venueId, resourceId, transformKeysToSnakeCase(updateResourcePayload, ['_delete'])));
  }

  if (shouldUpdateAllItemAvailabilities) {
    promises.push(...updateAllItemsAvailability(allItems, venueId, availabilityRecurrence));
  }

  if (currentCategory !== category) {
    const currentTagId = currentCategoryTag ? currentCategoryTag.id : null;
    const { id: newTagId } = category;

    if (currentTagId !== newTagId) {
      const oldTagIds = currentTagId && currentTagId !== foodAndBeverageL1Tag.id ? [currentTagId] : [];
      segment.event('Item Category change', { venueId, classId });
      tryToUpdateCategory(venue, classId, name, { newTagIds: [newTagId], oldTagIds });
    }
  }

  return Promise.all(promises);
};
